import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, EPaymentFrequency, EProvince, getEProvinceList } from '@if/core/types'
import { formatCurrency, formatDate, formatPercentage } from '@if/core/utils'
import { ControlledSelect, ControlledTextField, useTranslatedOptionEnum } from '@if/mui'
import { Calculate as CalculatorIcon } from '@mui/icons-material'
import { Box, Button, Card, CardContent, MenuItem, Stack, Typography } from '@mui/material'
import { useComputeProductsLoan } from '@src/data/api/calculator-api/calculator-api'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { CalculatorSchema, CalculatorSchemaType } from '@src/types/calculatorSchema'
import { ProductsComputeRequestDto } from '@src/types/ProductsComputeRequestDto'
import { getFirstPaymentOnMinMax } from '@src/types/WorksheetSchema'
import { memo, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const ProductsCalculator = () => {
  const { t } = useTranslation()

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<CalculatorSchemaType>({
    mode: 'onBlur',
    resolver: yupResolver(CalculatorSchema),
  })

  const [calculatorData, setCalculatorData] = useState<ProductsComputeRequestDto | undefined>(undefined)

  const [calculatedRate, isFetching] = useComputeProductsLoan(calculatorData)

  const onSubmit = (data: CalculatorSchemaType) => {
    if (!isValid) {
      return
    }

    const deliveryOn = formatDate(new Date())
    const calculationData = {
      stateIso: data?.stateIso ?? EProvince.Nunavut,
      amountRequested: data.amountRequested !== undefined ? data.amountRequested : 0,
      frequency: data.frequency!,
      term: data.terms!,
      deliveryOn,
      firstPaymentDate: formatDate(getFirstPaymentOnMinMax(deliveryOn, data.frequency ?? EPaymentFrequency.Monthly)[0]),
    }

    setCalculatorData(calculationData)
  }

  const showAnnualFees = (calculatedRate?.annualFees ?? 0) > 0
  const programConfig = programConfigByFinancingProgramId(EFinancingProgram.Products)
  const availableFrequencyPayments = programConfig.frequencyPayments

  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')
  const paymentFrequencyOptions = useTranslatedOptionEnum('EPaymentFrequency', availableFrequencyPayments, 'enum')
  const termsOptions = useMemo(
    () =>
      programConfig.termList.map((value: number) => (
        <MenuItem key={value} value={value}>{`${value} ${t('common.months')}`}</MenuItem>
      )),
    [t, programConfig],
  )

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" component="h1" gutterBottom>
          {t('calculator.title')}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing={2}>
            <ControlledSelect
              name="stateIso"
              control={control}
              options={provinceOptions}
              label="Province"
              disableEmptyOption
              fullWidth
            />
            <ControlledSelect
              name="frequency"
              control={control}
              options={paymentFrequencyOptions}
              label={t('calculator.frequency.label')}
              disableEmptyOption
              fullWidth
            />
            <ControlledSelect
              name="terms"
              control={control}
              options={termsOptions}
              label={t('calculator.terms')}
              disableEmptyOption
              fullWidth
            />
            <ControlledTextField
              label={t('calculator.amountRequested')}
              control={control}
              name="amountRequested"
              fullWidth
            />
            <Button
              type="submit"
              color="primary"
              variant="contained"
              fullWidth
              disabled={!isValid}
              loading={isFetching}
              endIcon={<CalculatorIcon />}
            >
              {t('calculator.calculate')}
            </Button>
          </Stack>

          {calculatedRate && (
            <Box mt={4} p={2} maxWidth={600} mx="auto">
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ textAlign: 'center' }}>
                    {/* Main message - loan payment */}
                    <Typography variant="h6" gutterBottom>
                      {t('calculator.monthlyPayment')}
                    </Typography>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {formatCurrency(calculatedRate.paymentAmountForFrequency)}
                    </Typography>

                    {/* Total interest costs */}
                    <Typography variant="body1" gutterBottom>
                      {t('calculator.totalInterestCosts', {
                        rate: formatPercentage(calculatedRate.interestRate),
                      })}
                    </Typography>

                    {/* Additional details */}
                    <Typography variant="body2" color="textSecondary">
                      {t('calculator.effectiveRate', { effectiveRate: calculatedRate.effectiveRate })}
                    </Typography>
                    {showAnnualFees && (
                      <Typography variant="body2" color="textSecondary">
                        {t('calculator.annualFees', { annualFees: calculatedRate.annualFees })}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </form>
      </CardContent>
    </Card>
  )
}

export default memo(ProductsCalculator)
